<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import Message from 'primevue/message';
import Skeleton from 'primevue/skeleton';
import { useAssignmentAiWorkspace } from '../../composables/useAssignmentAiWorkspace';
import { classifyRequestError, turnErrorCopy } from '../../utils/assignmentAiPresentation';
import AssignmentAiScopePicker from '../../components/assignment-ai/AssignmentAiScopePicker.vue';
import AssignmentAiStaleBanner from '../../components/assignment-ai/AssignmentAiStaleBanner.vue';
import AssignmentAiSessionHeader from '../../components/assignment-ai/AssignmentAiSessionHeader.vue';
import AssignmentAiClarificationBanner from '../../components/assignment-ai/AssignmentAiClarificationBanner.vue';
import AssignmentAiRulesPanel from '../../components/assignment-ai/AssignmentAiRulesPanel.vue';
import AssignmentAiConsolidatedInstruction from '../../components/assignment-ai/AssignmentAiConsolidatedInstruction.vue';
import AssignmentAiAmbiguitiesPanel from '../../components/assignment-ai/AssignmentAiAmbiguitiesPanel.vue';
import AssignmentAiTurnComposer from '../../components/assignment-ai/AssignmentAiTurnComposer.vue';
import AssignmentAiConflictBanner from '../../components/assignment-ai/AssignmentAiConflictBanner.vue';
import AssignmentAiSupportReference from '../../components/assignment-ai/AssignmentAiSupportReference.vue';
import AssignmentAiRequestErrorMessage from '../../components/assignment-ai/AssignmentAiRequestErrorMessage.vue';
import AssignmentAiDecisionConflictBanner from '../../components/assignment-ai/AssignmentAiDecisionConflictBanner.vue';
import AssignmentAiApplyResults from '../../components/assignment-ai/AssignmentAiApplyResults.vue';
import AssignmentAiRulesApprovalStrip from '../../components/assignment-ai/AssignmentAiRulesApprovalStrip.vue';
import AssignmentAiPendingStrip from '../../components/assignment-ai/AssignmentAiPendingStrip.vue';
import AssignmentAiProposalCard from '../../components/assignment-ai/AssignmentAiProposalCard.vue';
import AssignmentAiReviewBar from '../../components/assignment-ai/AssignmentAiReviewBar.vue';
import AssignmentAiApplyConfirmDialog from '../../components/assignment-ai/AssignmentAiApplyConfirmDialog.vue';
import AssignmentAiTitleCard from '../../components/assignment-ai/AssignmentAiTitleCard.vue';
import { RENDER_ALLOWED } from '../../stores/assignmentAi/instructionArtifact';

definePage({ meta: { title: 'AI Assignment' } });

const router = useRouter();
const {
  isAvailable,
  sessionId,
  store,
  decidedItems,
  pendingItems,
  items,
  heldSplit,
  startFromScope,
  reload,
  focusComposer,
  focusFirstHighlighted,
  composerRef,
} = useAssignmentAiWorkspace();

const confirmVisible = ref(false);

const detail = computed(() => store.detail);
// The store keeps the last session after its URL is left; the title card only describes the session in the URL.
const activeDetail = computed(() => (sessionId.value ? detail.value : null));
const isRestoring = computed(() => !!sessionId.value && !detail.value && !store.loadError);
const isStale = computed(() => store.status === 'Stale');
const blockedReason = computed(() => {
  if (store.status === 'Stale') return 'This session is stale and can no longer accept instructions.';
  if (store.status === 'Closed') return 'This session is closed.';
  if (store.status === 'Applying' || store.applyLocked) return 'Changes are being applied. Instructions are paused.';
  return store.turnBlockedReason ?? undefined;
});
const turnErrorText = computed(() => {
  if (store.retryAbortMessage) return store.retryAbortMessage;
  if (!store.turnError) return null;
  return store.turnError.code
    ? turnErrorCopy(store.turnError.code, store.turnError.httpStatus)
    : store.turnError.message || turnErrorCopy(undefined, store.turnError.httpStatus);
});
const orderedItems = computed(() => [...decidedItems.value, ...pendingItems.value]);
const canRender = computed(() => !!store.status && RENDER_ALLOWED.includes(store.status));
const lastTurnFailed = computed(() => !!store.turnError || !!store.conflict);
const canApplyAgain = computed(() => store.canApply && !!store.shownRun);
// 422 decision errors stay on their card; every other category is a workspace-level problem.
const workspaceDecisionError = computed(() => {
  const e = store.decisionError?.error;
  return e && classifyRequestError(e) !== 'invalidRequest' ? e : null;
});
const isDecisionSaving = computed(() => store.savingItemIds.size > 0);

function setComposer(el: unknown) {
  composerRef.current = (el as { focus: () => void } | null) ?? null;
}

// Restores only; never re-submits a turn. The banner is dismissed once the reload succeeded.
async function onApproveConflictReload() {
  await reload();
  if (!store.loadError) store.dismissApproveConflict();
}

async function onRetry() {
  await reload();
}

// Always lands on Assignment Review, even after a reload or from a shared link with no history to go back to.
function goToReview() {
  void router.push('/maintenance/assign/global');
}

function onNewSession() {
  store.reset();
  void router.push({ query: {} });
}

function openConfirm() {
  confirmVisible.value = true;
}

// The only caller of confirmApply: each explicit confirmation mints a new idempotency key.
async function onConfirmApply() {
  await store.confirmApply();
  confirmVisible.value = false;
}

// A retained attempt retries with the same key; otherwise a retry needs a fresh confirmation (and key). While apply is
// blocked there is nothing to confirm; the review bar already shows the reason.
function onResultsRetry() {
  if (store.applyAttempt) void store.retryApply();
  else if (store.canApply) openConfirm();
}

function onReReview() {
  store.highlightStale();
  void focusFirstHighlighted();
}
</script>

<template>
  <Page>
    <div class="flex h-full min-h-0 flex-col gap-2">
      <AssignmentAiTitleCard @back="goToReview">
        <template v-if="isAvailable && !sessionId" #badges>
          <span
            class="inline-flex h-6 shrink-0 items-center rounded-full border border-primary/20 bg-primary/10 px-2.5 text-xs font-bold tracking-wide text-primary dark:text-text-light"
          >
            New session
          </span>
        </template>
        <template v-if="activeDetail" #actions>
          <Button
            label="New session"
            icon="pi pi-plus"
            size="small"
            severity="secondary"
            outlined
            data-testid="new-session"
            @click="onNewSession"
          />
        </template>
        <template v-if="activeDetail" #default>
          <AssignmentAiSessionHeader :detail="activeDetail" />
        </template>
      </AssignmentAiTitleCard>

      <Message v-if="!isAvailable" severity="info" :closable="false">AI assignment is not available.</Message>

      <template v-else>
        <AssignmentAiScopePicker
          v-if="!sessionId"
          class="lg:min-h-0 lg:flex-1"
          :is-creating="store.isCreating"
          :create-error="store.createError"
          @start="startFromScope"
          @browse-tasks="goToReview"
        />

        <div v-else-if="isRestoring" class="flex flex-col gap-2" data-testid="restoring">
          <Skeleton height="3rem" />
          <Skeleton height="12rem" />
        </div>

        <Message v-else-if="store.loadError" severity="error" :closable="false" data-testid="load-error">
          <div class="flex flex-wrap items-center gap-3">
            <span>{{ store.loadError }}</span>
            <Button label="Retry" size="small" data-testid="retry" @click="onRetry" />
          </div>
        </Message>

        <template v-else-if="detail">
          <AssignmentAiStaleBanner v-if="isStale" />

          <AssignmentAiClarificationBanner
            :question="store.clarificationQuestion"
            :status="store.status"
            :reviewable="heldSplit.reviewable"
            :held="heldSplit.held"
            :kind="store.lastClarificationKind"
            @focus-composer="focusComposer"
          />

          <!-- Two card panes, like Assignment Review: each scrolls on its own and pins its main control in a footer
               (the instruction box on the left, the review bar on the right). -->
          <div class="grid grid-cols-1 gap-2 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(26rem,2fr)_minmax(0,3fr)]">
            <!-- Left: what the AI is told. Instructions in, rules and summary out. Approve rules sits beside Apply in the
                 review bar; the rules panel only offers it while there are no proposals (and so no review bar). -->
            <section
              class="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-background-card shadow-card lg:min-h-0"
              aria-labelledby="ai-instructions-heading"
            >
              <header class="shrink-0 border-b border-border px-3 py-2">
                <h2 id="ai-instructions-heading" class="text-sm font-semibold text-text">Instructions</h2>
                <p class="mt-1 text-xs text-secondary">
                  Tell the AI how to match tasks to task lists. Approve its rules before you apply.
                </p>
              </header>
              <div class="flex flex-col gap-4 p-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto" data-testid="ai-instructions">
                <AssignmentAiRulesPanel
                  :revision="detail.activeInstructionRevision"
                  :approving="store.isApproving"
                  :can-approve="store.canApprove"
                  :locked-reason="store.approveLockReason"
                  :rules-changed="store.rulesChangedNotice"
                  :approve-error="store.approveError"
                  :show-approve="items.length === 0"
                  @approve="store.approveInstructions()"
                  @reload="reload"
                />
                <AssignmentAiConsolidatedInstruction
                  class="min-w-0 border-t border-border pt-4"
                  :artifact="store.currentConsolidated"
                  :rendering="store.isRendering"
                  :submitting="store.isSubmitting"
                  :locked="store.isReviewLocked"
                  :render-error="store.renderError"
                  :can-render="canRender"
                  :last-turn-failed="lastTurnFailed"
                  :turn-source="store.lastTurnSource"
                  @render="store.renderConsolidated()"
                  @send-edit="store.submitConsolidatedEdit($event)"
                  @reload="reload"
                />
                <AssignmentAiAmbiguitiesPanel
                  class="border-t border-border pt-4"
                  :revision="detail.activeInstructionRevision"
                />
              </div>
              <footer class="flex shrink-0 flex-col gap-2 border-t border-border p-3">
                <AssignmentAiConflictBanner
                  v-if="store.conflict"
                  :attempted="store.conflict.attemptedMessage"
                  :current="store.conflict.current"
                  :correlation-id="store.conflict.correlationId"
                  :changed="store.conflict.changed"
                  @reload-retry="store.reloadAndRetry()"
                  @dismiss="store.dismissConflict()"
                />
                <!-- Reads the live session, so a later reload or turn is reflected while the banner is open. -->
                <AssignmentAiConflictBanner
                  v-if="store.approveConflict"
                  variant="approval"
                  :approval-category="store.approveConflict.category"
                  :current="store.detail"
                  @reload="onApproveConflictReload"
                  @dismiss="store.dismissApproveConflict()"
                />
                <Message v-if="turnErrorText" severity="error" :closable="false" data-testid="turn-error">
                  <div class="flex flex-col gap-1">
                    <span>{{ turnErrorText }}</span>
                    <AssignmentAiSupportReference
                      v-if="!store.retryAbortMessage"
                      :correlation-id="store.turnError?.correlationId"
                    />
                  </div>
                </Message>
                <AssignmentAiTurnComposer
                  :ref="setComposer"
                  v-model="store.draftMessage"
                  :can-submit="store.canSubmitTurn"
                  :is-submitting="store.isSubmitting"
                  :status="store.status"
                  :blocked-reason="blockedReason"
                  @submit="store.submitTurn()"
                />
              </footer>
            </section>

            <!-- Right: what the AI proposed, your review of it, and what actually happened. -->
            <section
              class="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-background-card shadow-card lg:min-h-0"
              aria-labelledby="ai-proposals-heading"
            >
              <header class="shrink-0 border-b border-border px-3 py-2">
                <h2 id="ai-proposals-heading" class="text-sm font-semibold text-text">Proposals</h2>
                <p class="mt-1 text-xs text-secondary">
                  Accept, edit, reject or lock each proposal, then apply the accepted ones.
                </p>
              </header>
              <div class="flex flex-col gap-3 p-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                <div v-if="workspaceDecisionError" data-testid="ai-decision-error">
                  <AssignmentAiRequestErrorMessage
                    :error="workspaceDecisionError"
                    :busy="isDecisionSaving"
                    @retry="store.retryDecision()"
                    @reload="reload"
                    @new-session="onNewSession"
                  />
                </div>
                <AssignmentAiDecisionConflictBanner
                  v-if="store.decisionConflict"
                  :conflict="store.decisionConflict"
                  :busy="isDecisionSaving || store.isReviewLocked"
                  @reapply="store.reapplyConflictChoices()"
                  @dismiss="store.dismissDecisionConflict()"
                />
                <AssignmentAiApplyResults
                  :run="store.shownRun"
                  :in-progress-run="store.applyInProgressRun"
                  :error="store.applyError"
                  :retained="!!store.applyAttempt"
                  :applying="store.isApplying"
                  :task-labels="store.taskLabels"
                  :task-list-labels="store.taskListLabels"
                  :can-apply-again="canApplyAgain"
                  :can-approve="store.canApprove"
                  @retry="onResultsRetry"
                  @check-again="store.retryApply()"
                  @cancel-retry="store.cancelApplyRetry()"
                  @reload="reload"
                  @approve="store.approveInstructions()"
                  @re-review="onReReview"
                  @apply-again="openConfirm"
                  @new-session="onNewSession"
                />
                <AssignmentAiRulesApprovalStrip
                  v-if="detail.activeProposalRevision"
                  :state="store.rulesApprovalState"
                  :rules-changed="store.rulesChangedNotice"
                  :outdated="store.proposalRulesOutdated"
                  :earlier-rules="store.proposalRulesEarlierWhilePending"
                />
                <AssignmentAiPendingStrip :items="pendingItems" />
                <div v-if="items.length === 0" class="flex flex-1 flex-col items-center justify-center gap-2 py-12 text-center">
                  <i class="fa-solid fa-wand-magic-sparkles text-2xl text-surface-400 dark:text-surface-500" aria-hidden="true" />
                  <p class="text-sm text-secondary">No proposals yet — send an instruction to generate them.</p>
                </div>
                <AssignmentAiProposalCard
                  v-for="item in orderedItems"
                  :key="`${item.id}:${store.activeProposalRevisionId ?? ''}`"
                  :item="item"
                />
              </div>
              <footer v-if="items.length > 0" class="shrink-0 border-t border-border p-3">
                <AssignmentAiReviewBar
                  :counts="store.counts"
                  :blocked-reason="store.applyBlockedReason"
                  :applying="store.isApplying"
                  :show-approve="store.instructionApprovalPending"
                  :approving="store.isApproving"
                  :can-approve="store.canApprove"
                  @apply="openConfirm"
                  @approve="store.approveInstructions()"
                />
              </footer>
            </section>
          </div>
        </template>
      </template>
    </div>

    <AssignmentAiApplyConfirmDialog
      v-if="detail"
      v-model:visible="confirmVisible"
      :preview="store.preview"
      :task-labels="store.taskLabels"
      :task-list-labels="store.taskListLabels"
      :applying="store.isApplying"
      @confirm="onConfirmApply"
    />
  </Page>
</template>
